import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import gfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Children, useEffect, useRef, useState, type JSX } from "react";
import clsx from "clsx";
import Smile from "../Smile";
import rehypeKatex from "lib/extra/modules/rehype-katex";

interface ContentRendererProps {
  children: string;
}

export default function ContentRenderer({
  children,
  ...rest
}: ContentRendererProps) {
  const theString = String(children);
  const match = /\/smile/.exec(theString || "");
  if (match) {
    const finalDOMs: (JSX.Element | undefined)[] = [];
    theString.split("/smile").forEach((str, i) => {
      const theSmile = "/smile" + str;
      if (i === 0) {
        finalDOMs.push(originalMarkdown({ children: theSmile, ...rest }));
      } else {
        finalDOMs.push(smileMarkdown({ children: theSmile, ...rest }));
      }
    });
    if (finalDOMs.length > 0) {
      return <>{finalDOMs}</>;
    }else{
      return <></>;
    }
  } else {
    return originalMarkdown({ children, ...rest });
  }
  
}

function smileMarkdown({ children, ...rest }: ContentRendererProps) {
  const theString = String(children);
  let theSmile = null;
  let preString = null;
  let postString = null;
  const theSmileIndex = theString.indexOf("/smile");
  theSmile = theString.substring(theSmileIndex);
  const firstSlash = theSmile.indexOf("/");
  const lastSlash = theSmile.lastIndexOf("smile/");
  theSmile = theSmile.substring(firstSlash + 1, lastSlash);
  const toFindSmile = "/" + theSmile + "/";
  theSmile = theSmile
    .replace(/\//g, "")
    .replace(/smile/g, "")
    .replace(/\n/g, "")
    .trim();
  preString = theString.substring(0, theSmileIndex);
  postString = theString.substring(
    theString.indexOf(toFindSmile) + toFindSmile.length
  );
  if (theSmile) {
    let preHTML = null;
    let postHTML = null;
    let finalHTML = (
      <Smile smileCode={theSmile} className="inline-block max-w-sm" />
    );
    if (preString) {
      preHTML = originalMarkdown({ children: preString, ...rest });
    }
    if (postString) {
      postHTML = originalMarkdown({ children: postString, ...rest });
    }
    return (
      <>
        {preHTML ? preHTML : null}
        {finalHTML ? finalHTML : null}
        {postHTML ? postHTML : null}
      </>
    );
  }
}

function originalMarkdown({ children, ...rest }: ContentRendererProps) {
  const theString = String(children);
  children = theString.replace(/\/smile/, "").replace(/smile\//, "");
  return (
    <ReactMarkdown
      components={{
        code({ node, inline, className, children, ...props }) {
          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        h1: ({ children }) => <h1 className="text-2xl font-black mt-5 mb-2 text-gray-900 dark:text-white">{children}</h1>,
        h2: ({ children }) => <h2 className="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white">{children}</h2>,
        h3: ({ children }) => <h3 className="text-lg font-semibold mt-3 mb-1 text-gray-800 dark:text-gray-100">{children}</h3>,
        ul({ node, sourcePosition, ...props }) {
          return <ul {...props} className="list-disc pl-4 py-2" />;
        },
        li({ node, sourcePosition, ...props }) {
          return <li {...props} className="py-2" />;
        },
         
        p: ({ node, sourcePosition, children, ...props }) => (
          <p
            data-row={sourcePosition?.start.line}
            data-col={sourcePosition?.start.column}
            {...props}
            className="m-0 text-15 remove-bottom"
            // changed margin bottom from my-1 to m-0 as it was causing issues with pdf generation page
          >
            {children}
          </p>
        ),
         
        table: ({ children }) => (
          <table className="table-auto border dark:border-gray-600 my-10 px-4 mx-auto w-full text-left rounded-lg overflow-hidden">
            {children}
          </table>
        ),

        thead: ({ children }) => (
          <thead className="border-b bg-gray-700 dark:bg-gray-900">{children}</thead>
        ),

        th: ({ children }) => (
          <th
            scope="col"
            className="text-xs md:text-sm font-semibold text-gray-100 md:px-6 md:py-4 px-2 py-4 text-left border-r border-gray-600"
          >
            {children}
          </th>
        ),

        tr: ({ children }) => <tr className="border-b border-gray-200 dark:border-gray-700 even:bg-gray-50 dark:even:bg-gray-800/50">{children}</tr>,

        td: ({ children }) => (
          <td className="text-xs md:text-sm text-gray-800 dark:text-gray-200 font-light md:px-6 md:py-4 whitespace-pre-line border-r border-gray-200 dark:border-gray-700 px-2 py-4">
            {children}
          </td>
        ),
      }}
      rawSourcePos
      remarkPlugins={[remarkMath, gfm]}
      rehypePlugins={[rehypeKatex, rehypeRaw]}
      {...rest}
    >
      {children}
    </ReactMarkdown>
  );
}
